import { applyMiddleware, legacy_createStore as createStore, combineReducers, compose, Middleware, Store } from 'redux';
import { thunk as reduxThunkMiddleware } from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { createBrowserHistory } from 'history';
import { createReduxHistoryContext, RouterActions } from 'redux-first-history';

import { isDevelop } from 'app/config';
import { tracker } from 'app/store/tracker';
import { ApiActions } from 'app/store/modules/api/actions';
import { reducer as apiReducer } from 'app/store/modules/api/reducer';
import { AuthActions } from 'app/store/modules/auth/actions';
import { reducer as authReducer } from 'app/store/modules/auth/reducer';
import { UserActions } from 'app/store/modules/user/actions';
import { reducer as userReducer } from 'app/store/modules/user/reducer';
import { BalanceActions } from 'app/store/modules/balance/actions';
import { reducer as balanceReducer } from 'app/store/modules/balance/reducer';
import { CryptoCompareActions, reducer as cryptoCompareReducer } from 'app/store/modules/crypto_compare';
import { State } from 'app/store/types';

// routing state lives in redux (state.router), and thunks navigate by dispatching `push`
const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

export type AllActions = ApiActions | AuthActions | BalanceActions | CryptoCompareActions | UserActions | RouterActions;

const middleware: Array<Middleware> = [routerMiddleware, reduxThunkMiddleware, tracker];
if (isDevelop) middleware.push(createLogger({ collapsed: true, timestamp: false }));

/**
 * Set up redux dev tools, if available.
 */
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const reducer = combineReducers({
  api: apiReducer,
  auth: authReducer,
  balance: balanceReducer,
  user: userReducer,
  cryptoCompare: cryptoCompareReducer,
  router: routerReducer,
});

export const configureStore = (): Store<State> =>
  createStore(reducer, composeEnhancers(applyMiddleware(...middleware)));

/** The history the router renders from, kept in step with the store. Call once, after creating the store. */
export const createHistory = (store: Store<State>) => {
  const history = createReduxHistory(store);
  history.listen(({ location }) => {
    window.analytics.track('router', {
      pathname: location.pathname,
      initialAppLoad: false,
    });
  });
  return history;
};
